import React, { Suspense, useEffect, useState, createContext, useContext, useRef, useMemo } from 'react';
import { useLeafletContext } from '@react-leaflet/core'
import * as L from 'leaflet';
import * as turf from '@turf/turf'
import { useTranslation } from 'react-i18next';

import {
  MapContainer,
  TileLayer,  
  LayersControl,
  useMapEvents,
  FeatureGroup,
  useMap,
  GeoJSON,
} from 'react-leaflet';
import { EditControl } from "react-leaflet-draw";
import { createBrowserHistory } from "history";

import { Layout, Spin } from 'antd';

// Import Global Context 
import {GlobalContext} from "../../App";

//Services
import GeoService from "../../services/GeoService";
import FieldsService from "../../services/Fields";
import SeasonService from "../../services/Season";

// Map Layers
import { MapLayers } from "../map-overlays/MapOverlays"
import { centroid } from '@turf/turf';
import { BaseViewContext } from '../../views/Base';
import { useLocation, useNavigate, useParams } from 'react-router';
import { convertBoundingBoxToPolygon } from '../../utilities/polygon_util';
import { getGeoJSONBounds } from '../../utilities/format';

import CCOverlay from '../crop-intelligence/CCOverlay';
import CCRegions from '../crop-intelligence/CCRegions';
import ParcelService from '../../services/ParcelService';
import AreaOverlay from './AreaOverlay';
import EditablePolygon from './EditablePolygon';
import TCIManager from './TCIManager';
import TCILayers from './TCILayers';


const center = [47.5621227, 1.0827152]

const AGCCMapContext = createContext("");

//FIXME - Repetition; check LeftSidebar.js, IndexTimeline.js, AddEditProvisions.js, agcc-map.js
const Loading = () => {
  return (
    <Layout className='view-loading-overlay' style={{ backgroundColor: "transparent" }}>
      <div className='view-loading-overlay-content'>
        <Spin />
      </div>
    </Layout>
  );
}

function MapSubscriber(props){
  
  const navigate = useNavigate();
  const location = useLocation();
  const [isFlyTo, setIsFlyTo] = useState(false);

  const {user} = useContext(BaseViewContext)
  const map = useMap();


  useEffect(()=>{
    if(user){
      if(user.organization?.extent) {
      const polygonFeature = convertBoundingBoxToPolygon(user.organization.extent);
      const center = centroid(polygonFeature.polygon).geometry.coordinates;
      const lat = center[1];
      const lng = center[0];
      map.flyTo([lat, lng], 8, {duration: "1"});
    } else {
      map.flyTo(center, 4, {duration: "1"});
    }
      //map.fitBounds(polygonFeature.polygon);
    }
  }, [map, user])



  useMapEvents({
    click: () => {
      //map.locate()
    },
    locationfound: (e) => {
      props.map.current.flyTo(e.latlng, props.map.getZoom())
    },
    moveend: () => {
      if (!isFlyTo) {
        // your code here
        // Update URL with long lat and zoom
        const newCenter = props.map.current.getCenter();
        const newZoom = props.map.current.getZoom();
        const url = new URL(window.location.href);
        url.searchParams.set("lat", newCenter.lat);
        url.searchParams.set("lng", newCenter.lng);
        url.searchParams.set("zoom", newZoom);
        const new_history_state = {title: `Lat ${newCenter.lat} Lng ${newCenter.lng}`, url: url.pathname + url.search + url.hash}
        //FIXME - This triggers whole HomeView all over again
        // add console.logs to HomeView and BaseView and see it in the console.
        window.history.replaceState(new_history_state, new_history_state.title, new_history_state.url);
        //navigate(new_history_state.url, {replace:false})
      } else {
        setIsFlyTo(false);
      } 
    },
    fly: () => {
      setIsFlyTo(true);
    },
    flyend: () => {
      setIsFlyTo(true);
      // your code here
    }
  })

  return (<></>)
}

/* WFS Component */
/* This will get GeoJSON data from GeoServer based on the extent the user is viewing currently */
function WFS(props) {

  const {selected_user_asset, setSelectedUserAsset, setSelectedIndexOverlay, setSelectedMapLayer, activeSeasons} = useContext(GlobalContext);
  const {setViewLoading} = useContext(BaseViewContext)
  const context = useLeafletContext()
  const wfs_ref = useRef()
  const [wfs_data, setWFSData] = useState(null);
  const [map_moved, setMapMoved] = useState(null);

  const location = useLocation();
  const {id} = useParams();

  const {user }= useContext(BaseViewContext);

  const focusOnField = (field_id) => {
    const bounds = getGeoJSONBounds(field_id);
    props.map_ref.current.fitBounds(bounds);
    // wfs_ref.current.eachLayer(function (layer) {  
    //   if(layer.feature.properties.id === field_id) {    
    //     const bounds = layer.getBounds();
        
    //     layer.setStyle({color: "red", fillColor: "gray"}) 
    //   }
    // });
  }

  useMapEvents({
    dragend: (event) => {
      setMapMoved(event); // this is a trick to ping the useEffect below to work again, to avoid repetition of the code
    },
  })

  useEffect(() => {
    // Don't fetch if organizationId is not available yet
    if (!props.org_id) {
      return;
    }
    
    const bounds = props.map_ref.current.getBounds();
    let ne = bounds.getNorthEast(); // Coords of the northeast corner
    let sw = bounds.getSouthWest(); // Coords of the southwest corner
    const extent = [sw.lng, sw.lat, ne.lng, ne.lat];
    
    const fetchWFS = async () => {
      setViewLoading(true);
      const data = await GeoService.getWFS(extent, props.layer_name, props.org_id)
      
      // Filter fields based on active seasons
      if (props.layer_name === 'fields') {
        if (activeSeasons.size > 0) {
          const filteredData = {
            ...data,
            features: data.features?.filter(feature => {
              // WFS query joins with seasons table and includes season_id
              const seasonId = feature.properties.season_id;
              return seasonId && activeSeasons.has(seasonId);
            }) || []
          };
          setWFSData(filteredData);
        } else {
          // If no seasons are active, show no fields
          setWFSData({ ...data, features: [] });
        }
      } else {
        setWFSData(data);
      }
      
      setViewLoading(false);
    }
    fetchWFS();
  }, [props.map_ref, map_moved, props.layer_name, props.org_id, setViewLoading, activeSeasons, user.organizationId])

  useEffect(() => {
    if(selected_user_asset) {
      focusOnField(selected_user_asset)
      setSelectedMapLayer(MapLayers[2])
    } 

    if(!selected_user_asset && props.map_ref?.current?.getZoom() > 15) {
      props.map_ref.current.zoomOut(2);
      setSelectedMapLayer(MapLayers[3])
    }
  }, [selected_user_asset])


  const onFeatureClick = async (e) => {
    const feature = e.target;
    const fieldId = feature.feature?.properties?.id;
    const seasonId = feature.feature?.properties?.season_id;

    if (selected_user_asset?.id === fieldId) { // second click on selected asset -> zoom out and clear
      feature.setStyle({ color: "blue", fillColor: "green" });
      props.map_ref.current.zoomOut(2);
      setSelectedUserAsset(null);
      return;
    }

    // Highlight and fit bounds as before
    feature.setStyle({ color: "red", fillColor: "blue", stroke: "purple" });
    const bounds = feature.getBounds();
    props.map_ref.current.fitBounds(bounds);

    // Fetch full field meta and attach current_season if missing
    try {
      setViewLoading(true);
      let fullField = await FieldsService.getFieldMeta(fieldId);
      if (!fullField?.current_season && seasonId) {
        try {
          const season = await SeasonService.getSeasonById(seasonId);
          fullField = { ...fullField, current_season: season };
        } catch (e2) {
          console.warn('Season fetch failed for field selection', fieldId, seasonId, e2);
        }
      }
      setSelectedUserAsset(fullField);
    } catch (err) {
      console.error('Failed to fetch field meta; falling back to minimal selection', err);
      setSelectedUserAsset({ ...feature.feature.properties, geometry: feature.feature.geometry });
    } finally {
      setViewLoading(false);
    }
  }

  useEffect(() => {
    // if(user.organization) { //TODO: INSPECT HERE FOR A WHILE
      wfs_ref.current = new L.geoJSON(wfs_data, {
        onEachFeature: (feature, layer) => {
          layer.on({
            click: onFeatureClick,
            //mouseover: layer.bindPopup(feature.properties.fid).openPopup()
          });
          layer.bindTooltip(feature.properties.name, {
            permanent: false,
            direction: 'center', // Direction can be 'center', 'top', 'bottom', 'left', or 'right'
            offset: [0, 0],
            className: 'feature-label'
          });
          //console.log("FEAUTER: ", feature);
        },
        style: (feature) => {
          const _style = {
            color: feature.properties?.color || "#F44336",
            fillColor: feature.properties?.color || "#FFCDD2" ,
          }
          //feature.setStyle(_style)
          return _style;
        }
      })

      props.map_ref.current.addLayer(wfs_ref.current)

      return () => {
        props.map_ref.current?.removeLayer(wfs_ref.current)
      }
    // }
  }, [props.map_ref, selected_user_asset, setSelectedIndexOverlay, setSelectedUserAsset, wfs_data, activeSeasons])

  return null
}

const AGCCMap = () => {
  const map_ref = useRef(null);
  const index_overlay_ref = useRef(null); // holds ref to the tilelayer when a vegetation index is loaded
  const [drawUIGeoJSON, setDrawUIGeoJSON] = useState(null);
  const [segmentation_id, setSegmentationID] = useState(null); // state to keep segmentation id from SAM API response
  const [segmented_mask, setSegmentedMask] = useState(null); // state to keep segmented mask from SAM API response
  // if segmentation progress is not 100, then show loading, see getSegmentation method
  const [is_segmentation_loading, setIsSegmentationLoading] = useState(false);
  const [map_center, setMapCenter] = useState({lat: 0, lng: 0, zoom: 2}); // map center at opening
  const [selected_feature, setSelectedFeature] = useState(null);
    // Main map state
    const [map, setMap] = useState(null);


  // Get current_view, selected_map_layer and query_params from Global Context
  // current_view is to be updated to Home
  // selected_map_layer Will be manipulated by MapOverlays Component
  // "../components/map-overlays/MapOverlays"
  // query_params is accessible by every component
  const { 
    selected_map_layer, 
    setSelectedMapLayer, 
    query_params, 
    selected_user_asset,
    setSelectedUserAsset,
    selected_index_overlay,
    globalFieldDrawUIEnabled,
    globalCCViewEnabled, 
    setGlobalCCViewEnabled,
    setDrawnField,
    global_map_center,
    setGlobalMapCenter,
    selected_index,
    fieldEditMode,
    setFieldEditMode,
    tciLayers,
    setTciLayers,
    activeSeasons,
    setActiveSeasons
  } = useContext(GlobalContext);

  const {current_view, user, view_loading, setViewLoading} = useContext(BaseViewContext);
  const { t } = useTranslation('translation');

  /* !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! */
  // Window resize hack
  // This is necessary for leaflet to load the map in full. 
  // Don't touch it, play or otherwise fuck around with it.
  const handleResize = () => {
    if(map_ref)
      map_ref.current.invalidateSize();
    else
      // not a map_ref
      console.log("Not a map_ref")
  };
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () =>  window.removeEventListener("resize", handleResize);
  },[])
  useEffect(() => {
    setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
    }, 100);
  }, [map_ref])
  /* !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! */

  //const _map_center = {lat: 39.0905, lng: 34.8638, zoom: 6.5};
  
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      if(query_params) {
       /* const lat = query_params.get('lat') ? query_params.get('lat') : 39.0905
        const lng = query_params.get('lng') ? query_params.get('lng') : 34.8638
        const zoom = query_params.get('zoom') ? query_params.get('zoom') : 6.5

        // Fly To the queried location
        map_ref.current.flyTo({lat: lat, lng: lng, zoom: zoom})*/
        isFirstRender.current = false
        return;
      } else {
        // s
      }
    }
  }, [globalFieldDrawUIEnabled, map_ref, query_params])

  useEffect(() => {
    if (index_overlay_ref.current && selected_index_overlay?.tile_url) {
      index_overlay_ref.current.setUrl(selected_index_overlay.tile_url);
    }
  }, [selected_index_overlay, index_overlay_ref]);

  /* TODO Add spinner in the center of the feature when it is selected with an index */
  // useEffect(() => {
  //   let spinner_marker = "";
  //   if(selected_index && !selected_index_overlay) {
  //     // Get the center point of the feature's geometry
  //     const centroid = turf.centroid(turf.polygon(selected_user_asset.geometry?.coordinates || selected_user_asset.feature.geometry.coordinates)) 
  //     const center = {lat: centroid.geometry.coordinates[1], lng: centroid.geometry.coordinates[0]}

  //     // Create a custom icon using the PNG image
  //     const customIcon = L.divIcon({
  //       className: 'custom-icon',
  //       html: '<div class="ant-spin ant-spin-lg ant-spin-spinning css-dev-only-do-not-override-1rqnfsa" aria-live="polite" aria-busy="true"><span class="ant-spin-dot ant-spin-dot-spin"><i class="ant-spin-dot-item"></i><i class="ant-spin-dot-item"></i><i class="ant-spin-dot-item"></i><i class="ant-spin-dot-item"></i></span></div>',
  //       iconSize: [32, 32], // Adjust the size of the icon as needed
  //       iconAnchor: [16, 16] // Position the icon's anchor point at its center
  //     });

  //     // Create a marker with the custom icon at the center point
  //     spinner_marker = L.marker(center, { icon: customIcon }).addTo(map_ref.current);
  //   } else if (selected_index && selected_index_overlay) {
  //     map_ref.removeLayer(spinner_marker) // FIXME - this is the problem; how to remove a marker?
  //   }
  // }, [selected_index, selected_user_asset, selected_index_overlay]) 

  const featuregroup_ref = useRef(null);
  const editcontrol_ref = useRef(null);

  useEffect(() => {
    if (globalFieldDrawUIEnabled) { // if draw ui is enabled, then activate Google Satellite basemap
      setSelectedMapLayer(MapLayers[2]);
    } else {
      setSelectedMapLayer(MapLayers[3]);
    }

    if (featuregroup_ref.current?.getLayers().length === 0 && drawUIGeoJSON) {
      L.geoJSON(drawUIGeoJSON).eachLayer((layer) => {
        if (
          layer instanceof L.Polyline ||
          layer instanceof L.Polygon ||
          layer instanceof L.Marker
        ) {
          if (layer?.feature?.properties.radius && featuregroup_ref.current) {
            new L.Circle(layer.feature.geometry.coordinates.slice().reverse(), {
              radius: layer.feature?.properties.radius,
            }).addTo(featuregroup_ref.current);
          } else {
            featuregroup_ref.current?.addLayer(layer);
          }
        }
      });
    }
  }, [drawUIGeoJSON, globalFieldDrawUIEnabled, setSelectedMapLayer]);

  // Removed segmentBBOX function - now using TCI functionality instead

  useEffect(() => {
    if(selected_user_asset && globalFieldDrawUIEnabled){
      // Only show field polygon in preview, not TCI rectangles
      if (selected_user_asset.feature && selected_user_asset.feature.geometry && selected_user_asset.feature.geometry.type === 'Polygon') {
        setDrawUIGeoJSON(selected_user_asset.feature.geometry);
        setDrawnField(selected_user_asset.feature.geometry);
      }
    } else {
      setDrawUIGeoJSON(null);
      setDrawnField(null);
    }
  }, [globalFieldDrawUIEnabled, selected_user_asset, setDrawnField])

  
  // FIXME - This doesn't work at all. Should activate marker editcontrol on parcel selected on leftsidebar, and polygon editcontrol on draw on leftsidebar.
  // useEffect(() => {
  //   //map_ref.current?.flyTo([0, 0], 2, {duration: "1"});
  //   const drawControl = editcontrol_ref.current?.leafletElement;
  //   if (!drawControl) {
  //     console.log("sligkndgmf")
  //     return;
  //   }

  //   if(globalFieldDrawUIEnabled === "parcel") {
  //     console.log("BURADADADADADADDADADAD");
  //     // to start with marker mode active
  //     drawControl._toolbars.draw._modes.marker.handler.enable();
  //   } else if (globalFieldDrawUIEnabled === "draw") {
  //     // to start with polygon mode:
  //     drawControl?._toolbars?.draw?._modes?.polygon?.handler.enable();
  //   }
  // }, [globalFieldDrawUIEnabled, editcontrol_ref])

  useEffect(() => {
    if (global_map_center) map_ref.current?.flyTo([global_map_center.lat, global_map_center.lng], global_map_center.zoom || 10, {duration: "1"});
  }, [global_map_center])

  // Fit to field bounds when entering edit mode
  useEffect(() => {
    if (fieldEditMode && selected_user_asset && selected_user_asset.geometry && map_ref.current) {
      setTimeout(() => {
        const coords = selected_user_asset.geometry.coordinates[0].map(coord => [coord[1], coord[0]]);
        const bounds = L.latLngBounds(coords);
        map_ref.current.fitBounds(bounds, { padding: [20, 20] });
      }, 100);
    }
  }, [fieldEditMode, selected_user_asset])

  // Clear TCI layers when exiting edit or draw modes
  useEffect(() => {
    if (!fieldEditMode && !globalFieldDrawUIEnabled) {
      setTciLayers([]);
    }
  }, [fieldEditMode, globalFieldDrawUIEnabled, setTciLayers])



  function handleChange(e) {
    const geo = featuregroup_ref.current?.toGeoJSON();
    if (e.layerType === "marker") { // TODO invoke TKGM and other countries API to pull the parcel info including geometry
      ParcelService.getParcelForPoint(e.layer.getLatLng(), (_parcel) => {
        setSelectedUserAsset({feature: _parcel});
        setDrawUIGeoJSON({feature: _parcel});
        setDrawnField({feature: _parcel});
      })
    } else if (e.layerType === "polygon") {
      if (geo?.type === 'FeatureCollection') {
        // Use the most recently drawn feature (last in array) instead of first
        const lastFeature = geo.features[geo.features.length - 1];
        setSelectedUserAsset({feature: {geometry: lastFeature.geometry}, properties: {}});
        setDrawUIGeoJSON(geo);
        setDrawnField(geo);
      } 
    }
  }

  function handleDrawStart(e) {
    if(e.layer === "rectangle") {

    }
  }

  useEffect(() => {
    if(map_ref.current && user) { 

      if(user.organization.extent) {
        const polygonFeature = convertBoundingBoxToPolygon(user.organization.extent);
        const center = centroid(polygonFeature.polygon).geometry.coordinates;
        const lat = center[1];
        const lng = center[0];
        map_ref.current?.flyTo([lat, lng], 8, {duration: "1"});
      } else {
        map_ref.current?.flyTo(center, 4, {duration: "1"});
      }
      //map_ref.current?.fitBounds(user.organization.extent);
    }
  }, [map_ref, user])

  /* Map Container Memo */
  // is used to have more control over user defined assets
  const MapContainerMemo = useMemo(
    () => (
      <MapContainer
        whenReady={(map)=>setMap(map)}
        ref={map_ref}
        doubleClickZoom={false}
        center={[map_center.lat, map_center.lng]}
        zoom={map_center.zoom} 
        className="map-container"
        zoomControl={false}
        maxZoom={18}
      >
        <MapSubscriber map={map_ref} setMapCenter={setMapCenter} current_view={current_view}/>
        {
          globalFieldDrawUIEnabled 
            ?
            <FeatureGroup ref={featuregroup_ref} style={{zIndex: 5000}}>
              <EditControl
                ref={editcontrol_ref}
                position="topright"
                onEdited={handleChange}
                onCreated={(e) => {
                  console.log('🎨 Main EditControl onCreated called with layerType:', e.layerType);
                  // Only handle polygon and marker creation, not rectangles (rectangles handled by TCIManager)
                  if (e.layerType === 'polygon' || e.layerType === 'marker') {
                    console.log('🎨 Main EditControl processing:', e.layerType);
                    handleChange(e);
                  } else {
                    console.log('🎨 Main EditControl ignoring:', e.layerType);
                  }
                }}
                onDeleted={handleChange}
                onDrawStart={handleDrawStart}
                draw={{
                  rectangle: false,
                  circle: false,
                  polyline: false,
                  polygon: true,
                  marker: {icon: new L.DivIcon({
                    iconSize: new L.Point(10, 10),
                    className: 'leaflet-div-icon leaflet-editing-icon my-own-class'
                })},
                  circlemarker: false,
                }}
              />
            </FeatureGroup>
            :
          ""
        }
          
        <LayersControl position="topright">
         
          
          {/* Basemaps */}
          {selected_map_layer && MapLayers.map((layer) => (
            <LayersControl.Overlay
              key={layer.name}
              name={layer.name}
              checked={selected_map_layer.name === layer.name}
            >
              {layer.layer}
            </LayersControl.Overlay>
          ))}

          {
            selected_index_overlay?.tile_url &&
            <TileLayer
              key={`index-overlay-${selected_index_overlay.date}`}
              zIndex={1010}
              ref={index_overlay_ref}
              attribution={false}
              url={selected_index_overlay.tile_url}
            />
          }

          {map_ref && !globalFieldDrawUIEnabled && globalCCViewEnabled && <>
            <CCRegions />
            <CCOverlay />
          </>}

          {/* User-defined assets - uploaded, drawn or parcel-assigned */}
          {
            map_ref && user && !fieldEditMode &&
            <WFS map_ref={map_ref} layer_name={"fields"} org_id={user.organizationId}/>
          }

          {/* Inactive field preview overlay (grayed-out) when selected field is filtered out */}
          {
            (() => {
              const geometry = selected_user_asset?.geometry || selected_user_asset?.feature?.geometry;
              const seasonId = selected_user_asset?.current_season?.id;
              const hasActive = activeSeasons && activeSeasons.size > 0;
              const visibleNormally = hasActive && seasonId && activeSeasons.has(seasonId);
              const shouldPreview = !!(selected_user_asset && geometry && !fieldEditMode && (!hasActive || !visibleNormally));
              if (!shouldPreview) return null;
              // i18n for tooltip
              const seasonName = selected_user_asset?.current_season?.name;
              const fieldName = selected_user_asset?.name || '';
              
              return (
                <GeoJSON
                  key={`inactive-preview-${selected_user_asset?.id || 'feature'}`}
                  data={{ type: 'Feature', properties: {}, geometry }}
                  style={{
                    color: '#94a3b8',
                    weight: 2,
                    dashArray: '6 4',
                    fillColor: '#e2e8f0',
                    fillOpacity: 0.35,
                  }}
                  onEachFeature={(feature, layer) => {
                    // Tooltip content and hover behavior
                    // Mouse events to enhance clarity and cursor pointer
                    layer.on('mouseover', (e) => {
                      try {
                        const el = e.target.getElement?.();
                        if (el) el.style.cursor = 'pointer';
                        e.target.setStyle({ fillOpacity: 0.45 });
                      } catch (err) {}
                    });
                    layer.on('mouseout', (e) => {
                      try {
                        e.target.setStyle({ fillOpacity: 0.35 });
                      } catch (err) {}
                    });
                    // Click to activate the season in filters and legend
                    layer.on('click', () => {
                      try {
                        if (seasonId && setActiveSeasons) {
                          const next = new Set(activeSeasons);
                          next.add(seasonId);
                          setActiveSeasons(next);
                        }
                      } catch (err) {}
                    });
                    // Bind tooltip late to include i18n
                    try {
                      const hint = t('map.activate_season_hint', { season: seasonName || '' });
                      const title = fieldName || '';
                      const html = `<div>${title}<br/>${hint}</div>`;
                      layer.bindTooltip(html, { permanent: false, direction: 'top', opacity: 0.9, sticky: true });
                    } catch (err) {}
                  }}
                />
              );
            })()
          }

          {/* Editable polygon when in edit mode */}
          {
            fieldEditMode && selected_user_asset &&
            <EditablePolygon 
              key={`edit-polygon-${selected_user_asset.id}`}
              field={selected_user_asset} 
            />
          }

          {/* TCI Manager for rectangle drawing and TCI loading */}
          {
            (fieldEditMode || globalFieldDrawUIEnabled) &&
            <TCIManager />
          }

          {/* TCI Layers display */}
          {
            map_ref &&
            <TCILayers />
          }

          {
             map_ref && user && globalFieldDrawUIEnabled === "parcel" && selected_user_asset && 
             <GeoJSON data={selected_user_asset.feature} /> 
          }
        

          {/** FIXME - Delete if unnecessary, refactor if necessary - WAs used to show SAM segmentation results on the map  */}
          {
            // map_ref && globalFieldDrawUIEnabled &&
            // <WFS map_ref={map_ref} layer_name={"segmentations"} org_id="Segmentations" />
          } 
            
          
        </LayersControl>
         
      </MapContainer>
    ),[map_center.lat, 
      map_center.lng, 
      map_center.zoom, 
      globalFieldDrawUIEnabled,
      globalCCViewEnabled,
      handleChange, 
      current_view, 
      selected_index_overlay, 
      user, 
      selected_map_layer.name,
      selected_user_asset,
      fieldEditMode,
      activeSeasons]
  )


  return (
    <AGCCMapContext.Provider value={{map, setMap, map_center, setMapCenter}}>
      { /* MAP Content */ }
      <Layout>
        <Suspense fallback={<div>Loading...</div>}>
          {//map ? <DisplayPosition map={map} /> : null
          }

        {
          view_loading && <Loading />
        }
        {fieldEditMode && selected_user_asset && (
          <AreaOverlay 
            key={`area-${selected_user_asset.id}-${JSON.stringify(selected_user_asset.geometry)}`}
            geometry={selected_user_asset.geometry}
            top={72}
          />
        )}
        {MapContainerMemo}
       
        </Suspense>
        {
        //<Footer style={footerStyle}>Footer</Footer>
        }
      </Layout>
    </AGCCMapContext.Provider>
  );
};

export default AGCCMap;
